/** @format */
/* eslint-disable */

import {
  IS_MOBILE_PHONE,
  IS_NOT_PRODUCTION,
  IS_PRODUCTION,
} from "../constants";
import { type ToastOptions } from "react-hot-toast";

declare global {
  interface Array<T> {
    first(defaultValue?: T): T;
    last(defaultValue?: T): T;
  }
}

if (IS_PRODUCTION) {
  // globalThis.console.error = Sentry.captureException;
  globalThis.console.error = () => null;
  globalThis.console.warn = () => null;
  globalThis.console.info = () => null;
  globalThis.console.log = () => null;
}

export const DEFAULT_ERROR_MESSAGE = "Please refresh the page and try again.";

export const handleError = (error?: Error, message = ""): void => {
  logDev(error);
  message && logDev(message);

  if (error?.message?.trim() === "The operation was aborted.") {
    notify({
      level: "info",
      title: "Cancelled",
    });
    return;
  }

  if (IS_PRODUCTION || IS_MOBILE_PHONE) {
    // Sentry.captureMessage(message);
    // Sentry.captureException(error);
  }
  notify({
    level: "error",
    title: `Error in Rates Visualizer. ${message || DEFAULT_ERROR_MESSAGE}`,
    // message: message || "",
  });
};

if (!Array.prototype.first) {
  Array.prototype.first = function <T>(this: T[], defaultValue?: T) {
    // NOSONAR
    return this[0] || (defaultValue as T);
  };
}

if (!Array.prototype.last) {
  Array.prototype.last = function <T>(this: T[], defaultValue?: T) {
    // NOSONAR
    return this[this.length - 1] || (defaultValue as T);
  };
}

// const TADA_AUDIO = new Audio("https://freesound.org/data/previews/397/397353_4284968-lq.mp3");
// TADA_AUDIO.load();

const ALERT_ICONS = {
  info: "💡",
  warning: "⚠️",
  success: undefined,
  error: undefined,
};

export const notify = async ({
  id,
  level,
  title,
  message,
  tada: _tada,
  duration: _duration,
  onClick: _onClick,
}: {
  id?: string;
  level: keyof typeof ALERT_ICONS;
  title: string | React.JSX.Element;
  message?: string;
  tada?: boolean;
  duration?: number;
  onClick?: () => void;
}): Promise<string> => {
  const { default: toast } = await Promise.resolve(import("react-hot-toast"));

  const icon = ALERT_ICONS[level];

  const options = {
    id:
      id ||
      (typeof title === "string" ? `${level}-${title}-${message}` : undefined),
    position: "bottom-center",

    icon,
    // position: IS_MOBILE_PHONE ? toast.POSITION.TOP_CENTER : toast.POSITION.TOP_RIGHT,
    // autoClose: duration === 0 ? false : duration || undefined,
    // theme: "colored",
    // pauseOnHover: true,
    // pauseOnFocusLoss: true,
    // closeOnClick: true,
    // closeButton: false,
    // type: level,
    // onOpen: () => {
    //     if (tada) {
    //         // TADA_AUDIO && TADA_AUDIO.play().catch(console.error);
    //     }
    // },
  } as ToastOptions;

  const notification =
    message && typeof title === "string" ? `${title} ${message}` : title;

  if (level === "success") {
    return toast.success(notification, options);
  } else if (level === "error") {
    return toast.error(notification, options);
  } else {
    return toast(notification, options);
  }
};

export const withTadas = (string: string) => `🎉 ${string} 🎉`;

export const getId = (obj: { id: number }): number => obj.id;
export const logDev = (...args: unknown[]) => {
  if (IS_NOT_PRODUCTION) {
    const [message, ...extra] = args;

    if (message instanceof DOMException) {
      console.error(message);
    } else if (typeof message === "string") {
      console.log(`(dev) ${message}`, ...extra);
    } else {
      console.log("(dev)", message, ...extra);
    }
  }
};

export const isNumber = (value: unknown): value is number =>
  typeof value === "number" && isFinite(value);

export const isNumeric = (
  string: string | number | null | undefined,
): boolean => {
  if (!string && typeof string !== "number") return false;

  if (typeof string === "number") {
    return true;
  }

  if (typeof string !== "string") {
    return false;
  }

  return !!/\d+/.exec(string);
};

export const flatten = <T>(arrays: T[][]): T[] => {
  return ([] as T[]).concat(...arrays);
};

export const withNumberSuffix = (n: number) => {
  const s = String(n);
  if (!s) return n;
  if (n <= 0) return n;
  if (s.endsWith("1") && n !== 11) return `${s}st`;
  if (s.endsWith("2") && n !== 12) return `${s}nd`;
  if (s.endsWith("3") && n !== 13) return `${s}rd`;
  return `${s}th`;
};

export const titleize = (string: string, separator = " ", joiner = " ") => {
  if (!string) return "";

  const words = string.toLowerCase().split(separator);

  const toJoin = [];
  for (const word of words) {
    if (word) {
      toJoin.push(word[0].toUpperCase() + word.substring(1));
    }
  }
  return toJoin.join(joiner);
};

export const findClosestNumber = (arr: number[], target: number): number => {
  return arr.reduce((prev, curr) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev,
  );
};

export const isTruthy = <T>(
  thing: T,
): thing is Exclude<
  T,
  null | undefined | 0 | "" | false | typeof document.all
> => Boolean(thing);

export const sum = (array: number[]) =>
  array.reduce((acc, item) => acc + item, 0);

export const sumBy = <T extends Record<string, any>>(
  array: T[],
  key: keyof T,
): number =>
  array.reduce((acc, item) => acc + (item[key] as unknown as number), 0);

export const maxBy = <T extends Record<string, any>>(
  array: T[],
  key: keyof T | ((item: T) => number),
): T => {
  if (!array?.length) {
    return null as unknown as T;
  }

  let max =
    typeof key === "function"
      ? key(array[0])
      : (array[0][key] as unknown as number);
  let maxItem = array[0];
  if (typeof key === "function") {
    for (const item of array) {
      const value = key(item);
      if (value > max) {
        max = value;
        maxItem = item;
      }
    }
  } else {
    for (const item of array) {
      const value = item[key] as unknown as number;
      if (value > max) {
        max = value;
        maxItem = item;
      }
    }
  }
  return maxItem as T;
};

export const uniq = <T>(array: T[]): T[] => Array.from(new Set(array));

export const isEmpty = (thing: unknown): boolean => {
  if (!thing) {
    return true;
  } else if (Array.isArray(thing)) {
    return !thing.length;
  } else if (typeof thing === "object") {
    return !Object.keys(thing as Record<string, unknown>).length;
  } else {
    return false;
  }
};

/** Divides an array into smaller arrays (chunks) of a specified size.
 *
 * @param array The array to chunk. It can be of any type T.
 * @param chunkSize The maximum size of each chunk. Must be a positive integer.
 * @returns An array of arrays, where each inner array is a chunk.
 */
export const chunk = <T>(array: T[], chunkSize: number): T[][] => {
  // Input validation
  if (!array || !Array.isArray(array)) {
    console.error("Input must be a valid array.");
    return [];
  }
  if (chunkSize <= 0 || !Number.isInteger(chunkSize)) {
    console.error("Chunk size must be a positive integer.");
    return [array.slice()]; // Return a copy of the original array in one chunk
  }

  const result: T[][] = [];

  // Iterate through the array, taking steps equal to the chunkSize
  for (let i = 0; i < array.length; i += chunkSize) {
    // Use the slice method to grab a chunk from the current index (i)
    // up to, but not including, i + chunkSize.
    const chunk = array.slice(i, i + chunkSize);
    result.push(chunk);
  }

  return result;
};

export const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
